import React, { useState } from "react";
import { supportAPI } from "../services/api";
import toast from "react-hot-toast";
import {
  HiOutlineTicket,
  HiOutlineSearch,
  HiOutlineMail,
  HiOutlineCheck,
} from "react-icons/hi";

/**
 * Public page — customers check the status of their ticket using the
 * unique ticket number (no login required).
 */
const TrackTicket = () => {
  const [ticketNumber, setTicketNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    const num = ticketNumber.trim().toUpperCase();
    if (!num) return toast.error("Please enter your ticket number");
    setLoading(true);
    setNotFound(false);
    setTicket(null);
    try {
      const response = await supportAPI.publicStatus(num);
      setTicket(response.data?.data || null);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(error.response?.data?.message || "Failed to fetch ticket status");
      }
    } finally {
      setLoading(false);
    }
  };

  const priorityColor = {
    low: "bg-gray-100 text-gray-700",
    medium: "bg-blue-50 text-blue-700",
    high: "bg-amber-50 text-amber-700",
    urgent: "bg-red-50 text-red-700",
  }[ticket?.priority] || "bg-gray-100 text-gray-700";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-10">
      {/* Header */}
      <div className="w-full max-w-2xl text-center mb-8">
        <div className="w-14 h-14 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HiOutlineTicket className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Track Your Ticket
        </h1>
        <p className="text-gray-500 mt-2 text-sm sm:text-base">
          Enter the ticket number you received (e.g., TKT-260912-4F7K) to see the
          latest status and updates.
        </p>
      </div>

      {/* Search form */}
      <form
        onSubmit={handleSearch}
        className="w-full max-w-2xl flex flex-col sm:flex-row gap-2 bg-white p-3 rounded-2xl shadow-sm border border-gray-100"
      >
        <div className="flex-1 flex items-center gap-2 px-3">
          <HiOutlineSearch className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            placeholder="TKT-XXXXXX-XXXX"
            className="w-full py-2.5 outline-none text-gray-900 placeholder-gray-400 uppercase"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="bg-primary-500 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-600 disabled:opacity-60 transition-colors"
        >
          {loading ? "Checking..." : "Check Status"}
        </button>
      </form>

      {/* Not found */}
      {notFound && (
        <div className="w-full max-w-2xl mt-6 bg-red-50 border border-red-100 rounded-2xl p-5 text-center">
          <p className="text-red-700 font-medium">Ticket not found</p>
          <p className="text-red-600/80 text-sm mt-1">
            Please double-check the ticket number and try again.
          </p>
        </div>
      )}

      {/* Result */}
      {ticket && (
        <div className="w-full max-w-2xl mt-6 space-y-4">
          {/* Status card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <p className="font-mono text-sm font-bold text-primary-700">
                  {ticket.ticketNumber}
                </p>
                <h2 className="text-lg font-semibold text-gray-900 mt-0.5">
                  {ticket.subject}
                </h2>
              </div>
              {ticket.stage && (
                <span
                  className="self-start px-3 py-1.5 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: `${ticket.stage.color}20`,
                    color: ticket.stage.color,
                  }}
                >
                  {ticket.stage.name}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-500 text-xs">Priority</p>
                <span
                  className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${priorityColor}`}
                >
                  {ticket.priority}
                </span>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Category</p>
                <p className="font-medium text-gray-800 mt-1">
                  {ticket.category?.name || "—"}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Created on</p>
                <p className="font-medium text-gray-800 mt-1">
                  {new Date(ticket.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Resolved on</p>
                <p className="font-medium text-gray-800 mt-1">
                  {ticket.resolvedAt
                    ? new Date(ticket.resolvedAt).toLocaleDateString()
                    : "Pending"}
                </p>
              </div>
            </div>

            {ticket.mergedFrom && (
              <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm text-blue-700">
                This ticket number was merged into a combined ticket. The updates
                below are from the combined ticket.
              </div>
            )}
          </div>

          {/* Customer-safe timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Updates & History</h3>
            {ticket.timeline?.length === 0 ? (
              <p className="text-sm text-gray-500">
                No updates yet. We'll notify you when there's progress.
              </p>
            ) : (
              <div className="space-y-3">
                {[...(ticket.timeline || [])].reverse().map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                        {item.type === "resolved" ? (
                          <HiOutlineCheck className="w-4 h-4 text-green-600" />
                        ) : item.type === "comment" ? (
                          <HiOutlineMail className="w-4 h-4 text-primary-600" />
                        ) : (
                          <HiOutlineMail className="w-4 h-4 text-primary-600" />
                        )}
                      </div>
                      {i < ticket.timeline.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-100 my-1" />
                      )}
                    </div>
                    <div className="pb-1">
                      <p className="text-sm text-gray-800">{item.message}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {new Date(item.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-10">
        © {new Date().getFullYear()} TeleValley — Support Ticket Status
      </p>
    </div>
  );
};

export default TrackTicket;